import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { Skeleton } from '@shipt/design-system-loading'
import { range } from '@/features/shared/utils/range'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout/container'
import { Shelf } from '@/features/shop/components/Shelf'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { NextLink } from '@/features/shared/elements/Link'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useQueryKits } from '@/features/shop/services/Kits/queries'
import { useStoreId } from '@/features/account/services/User/hooks'
import { routes, getRetailerKitsUrl } from '@shared/constants/routes'
import { type ParamsOf } from '@next-types/routes'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { trackElementViewed, trackElementClicked } from '@/features/shared/analytics/element'

type Props = Pick<PlaceholderData, 'id' | 'content_type_id'> & {
  index?: number
}

export const KitsShelf = ({ id, content_type_id, index = 0 }: Props) => {
  const params = useParams<ParamsOf<'/shop/[store_slug]'>>()
  const storeId = useStoreId()

  const isRetailerContext = !!params?.store_slug
  const storeIdOverride = isRetailerContext ? storeId : undefined

  const kitDetailBaseUrl = isRetailerContext
    ? getRetailerKitsUrl(params.store_slug)
    : routes.KIT_DETAIL.url

  const seeAllUrl = isRetailerContext ? getRetailerKitsUrl(params.store_slug) : routes.KITS.url

  const { data, isLoading } = useQueryKits({ storeIdOverride })

  if (!isLoading && !data?.kits.length) return null

  return (
    <ScrollIntoView
      onInView={() =>
        trackElementViewed({
          display_format: 'shelf',
          display_sublocation: 'shipt_kits',
          shelf_index: index,
          content: 'Kits',
        })
      }
    >
      <GridLayoutContainer {...getCMSWrapperProps({ id, content_type_id })}>
        <Row align="center" justify="space-between" marginBottom="lg" marginTop="md">
          <Headline as="h2">Explore Shipt Kits</Headline>
          <TextButtonLink
            href={seeAllUrl}
            aria-label="See all kits"
            onClick={() =>
              trackElementClicked({
                display_format: 'button',
                display_sublocation: 'shipt_kits',
                content: 'View All',
              })
            }
          >
            See all
          </TextButtonLink>
        </Row>
        <Shelf hideTitle title="Kits">
          {isLoading
            ? range(4).map((i) => (
                <Column key={i} spacing="xxs">
                  <KitShelfImageWrapper>
                    <Skeleton height="100%" />
                  </KitShelfImageWrapper>
                  <Skeleton height={20} width={140} />
                  <Skeleton height={16} width={180} />
                </Column>
              ))
            : data?.kits.map((kit, listIndex) => (
                <ScrollIntoView
                  key={kit.id}
                  onInView={() =>
                    trackElementViewed({
                      display_format: 'kit_card',
                      display_sublocation: 'shipt_kits',
                      shelf_index: index,
                      list_index: listIndex,
                      content: `${kit.id}_${kit.name}`,
                    })
                  }
                >
                  <KitShelfCard
                    href={`${kitDetailBaseUrl}/${kit.id}`}
                    onClick={() =>
                      trackElementClicked({
                        display_format: 'kit_card',
                        display_sublocation: 'shipt_kits',
                        shelf_index: index,
                        list_index: listIndex,
                        content: `${kit.id}_${kit.name}`,
                      })
                    }
                  >
                    <KitShelfImageWrapper>
                      <Image
                        src={kit.image_url}
                        alt=""
                        fill
                        sizes="220px"
                        style={{ objectFit: 'cover' }}
                        fallbackType="generic"
                      />
                    </KitShelfImageWrapper>
                    <Column spacing="xxs" paddingTop="sm">
                      <Body strong>{kit.name}</Body>
                    </Column>
                  </KitShelfCard>
                </ScrollIntoView>
              ))}
        </Shelf>
      </GridLayoutContainer>
    </ScrollIntoView>
  )
}

const KitShelfCard = styled(NextLink)`
  display: block;
  width: 220px;
  text-decoration: none;
  margin-bottom: 0.5rem;
`

const KitShelfImageWrapper = styled.div`
  position: relative;
  width: 220px;
  height: 141px;
  border-radius: 1rem;
  overflow: hidden;
  background-color: ${({ theme }) => theme.gray100};
`
